import React, { useRef, useState } from 'react';
import { RequiredRule, Validator } from 'devextreme-react/validator';
import ValidationGroup from 'devextreme-react/validation-group';
import { QRCodeSVG } from 'qrcode.react';

import { LLBtn2, LLInput, llNotifier, withCommon } from '../../../../../components';

const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2';
const APP_STORE_URL = 'https://apps.apple.com/app/google-authenticator/id388497605';

const isIOSDevice = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const ua = navigator.userAgent || '';
    return (
        /iPad|iPhone|iPod/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
};

const buildOtpauthUrl = ({ issuer, username, secret }) => {
    const encodedIssuer = encodeURIComponent(issuer || '');
    const encodedAccount = encodeURIComponent(username || '');
    const encodedSecret = encodeURIComponent(secret || '');

    return `otpauth://totp/${encodedIssuer}:${encodedAccount}?secret=${encodedSecret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;
};

const copyText = async (value) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const el = document.createElement('textarea');
    el.value = value;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
};

const GoogleAuthQRCodeForm = withCommon({
    store: ({ store }) => ({
        siteName: store.site.siteName,
        username: store.user.username,
        viewSize: store.site.status.viewSize,
        setGoogleAuth: store.user.setGoogleAuth,
        googleQrCodeUserSecret: store.user.googleQrCodeUserSecret,
        isGoogleAuthActive: store.user.isGoogleAuthActive,
        isGoogleAuthSettingLoading: store.user.isGoogleAuthSettingLoading,
    }),
    defaultName: 'GoogleAuthQRCodeForm',
    isObserver: true,
    isTrans: true,
})(
    ({
        siteName,
        username,
        viewSize,
        setGoogleAuth,
        googleQrCodeUserSecret,
        isGoogleAuthActive,
        isGoogleAuthSettingLoading,
        trans,
    }) => {
        const [authCode, setAuthCode] = useState('');
        const isMobile = viewSize === 'mobile';
        const isIOS = isIOSDevice();
        const otpauthUrl = buildOtpauthUrl({
            issuer: siteName,
            username,
            secret: googleQrCodeUserSecret,
        });

        const setGoogleAuthRequest = () =>
            codeInputRef.current.instance.validate()?.isValid &&
            setGoogleAuth({
                active: !isGoogleAuthActive,
                code: authCode,
            }).then(() => {
                codeTextboxRef.current.instance.reset();
                codeInputRef.current.instance.reset();
            });

        const copySecret = () => {
            copyText(googleQrCodeUserSecret)
                .then(() => {
                    llNotifier({
                        message: 'TFA_SECRET_COPIED',
                        type: 'success',
                    });
                })
                .catch(() => {
                    llNotifier({
                        message: 'GENERAL_ERROR',
                        type: 'error',
                    });
                });
        };

        const codeInputRef = useRef(null),
            codeTextboxRef = useRef(null);

        const storeBadges = (
            <div className="google-auth__install-links-box">
                {!isIOS && (
                    <a
                        className="google-auth__install-link google-auth__install-link--google"
                        href={PLAY_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={trans('TFA_GET_THE_APP')}
                    />
                )}
                {(isIOS || !isMobile) && (
                    <a
                        className="google-auth__install-link google-auth__install-link--apple"
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={trans('TFA_GET_THE_APP')}
                    />
                )}
            </div>
        );

        const secretBlock = (labelKey = 'TFA_OR_COPY_SECRET') => (
            <div className="google-auth__secret">
                <p className="google-auth__label">{trans(labelKey)}</p>
                <div className="google-auth__secret-row">
                    <code className="google-auth__secret-value">
                        {googleQrCodeUserSecret}
                    </code>
                    <LLBtn2
                        type="button"
                        onClick={copySecret}
                        customClasses="google-auth__copy-btn"
                    >
                        {trans('TFA_COPY_SECRET')}
                    </LLBtn2>
                </div>
            </div>
        );

        const codeForm = (labelKey) => (
            <ValidationGroup className="google-auth__form" ref={codeInputRef}>
                {labelKey ? (
                    <p className="google-auth__label">{trans(labelKey)}</p>
                ) : null}
                <LLInput
                    ref={codeTextboxRef}
                    customClasses="google-auth__input"
                    disabled={isGoogleAuthSettingLoading}
                    maxLength={6}
                    mode="text"
                    id="google-auth__code"
                    name="google-auth__code"
                    value={authCode}
                    showClearButton={true}
                    onEnterKey={setGoogleAuthRequest}
                    onValueChanged={(e) => setAuthCode(e.value)}
                    stylingMode="outlined"
                >
                    <Validator>
                        <RequiredRule message={trans('This field is required')} />
                    </Validator>
                </LLInput>
                <LLBtn2
                    type="button"
                    onClick={setGoogleAuthRequest}
                    customClasses="google-auth__submit-btn"
                    isLoading={isGoogleAuthSettingLoading}
                >
                    {trans(isGoogleAuthActive ? 'Turn Off' : 'Turn On')}
                </LLBtn2>
            </ValidationGroup>
        );

        if (isGoogleAuthActive) {
            return codeForm('TFA_TURN_OFF_HINT');
        }

        if (isMobile) {
            return (
                <div className="google-auth__setup google-auth__setup--mobile">
                    <a className="ll-btn2 google-auth__add-link" href={otpauthUrl}>
                        <span className="ll-btn__content active">
                            {trans('TFA_ADD_TO_AUTHENTICATOR')}
                        </span>
                    </a>
                    <p className="google-auth__hint">{trans('TFA_ADD_HINT')}</p>
                    <div className="google-auth__fallback">
                        <p className="google-auth__install-text">
                            {trans('TFA_DONT_HAVE_APP')}
                        </p>
                        {storeBadges}
                        {isIOS ? (
                            <p className="google-auth__hint">{trans('TFA_ADD_HINT_IOS')}</p>
                        ) : null}
                        {secretBlock()}
                    </div>
                    <div className="google-auth__step google-auth__step--code">
                        <p className="google-auth__label">{trans('TFA_STEP_CODE')}</p>
                        {codeForm()}
                    </div>
                </div>
            );
        }

        return (
            <ol className="google-auth__steps">
                <li className="google-auth__step">
                    <p className="google-auth__step-title">
                        <span className="google-auth__step-num">1</span>
                        {trans('TFA_STEP_INSTALL')}
                    </p>
                    <p className="google-auth__hint">{trans('TFA_ANY_APP')}</p>
                    {storeBadges}
                </li>
                <li className="google-auth__step">
                    <p className="google-auth__step-title">
                        <span className="google-auth__step-num">2</span>
                        {trans('TFA_STEP_ADD')}
                    </p>
                    <div className="google-auth__qr-img-container">
                        <p className="google-auth__label">{trans('TFA_SCAN_QR')}</p>
                        <div className="google-auth__qr-wrap">
                            <QRCodeSVG
                                value={otpauthUrl}
                                bgColor="#fff"
                                fgColor="#000"
                                size={200}
                                marginSize={2}
                            />
                        </div>
                    </div>
                    {secretBlock()}
                </li>
                <li className="google-auth__step google-auth__step--code">
                    <p className="google-auth__step-title">
                        <span className="google-auth__step-num">3</span>
                        {trans('TFA_STEP_CODE')}
                    </p>
                    {codeForm()}
                </li>
            </ol>
        );
    }
);

export default GoogleAuthQRCodeForm;
